import React, { useState, useEffect } from 'react';
import { API_URL } from '../services/api';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Paper,
  Skeleton,
  Breadcrumbs,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from 'axios';

interface NewsDetail {
  id: number;
  title: string;
  content: string;
  featured_image: string;
  published_at: string;
  views: number;
  author_name: string;
  categories: Array<{ id: number; name: string }>;
  images: Array<{ id: number; image_url: string; caption: string }>;
}

const NoticiaDetalle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [news, setNews] = useState<NewsDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchNewsDetail();
    }
  }, [slug]);

  const fetchNewsDetail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news/${slug}`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Skeleton variant="text" sx={{ fontSize: '3rem' }} />
        <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mb: 4 }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
      </Container>
    );
  }

  if (!news) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Noticia no encontrada
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/noticias"
        >
          Volver a noticias
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          to="/"
          style={{
            color: 'inherit',
            textDecoration: 'none',
            opacity: 0.7,
          }}
        >
          Inicio
        </Link>
        <Link
          to="/noticias"
          style={{
            color: 'inherit',
            textDecoration: 'none',
            opacity: 0.7,
          }}
        >
          Noticias
        </Link>
        <Typography color="text.primary">{news.title}</Typography>
      </Breadcrumbs>

      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        component={Link}
        to="/noticias"
        sx={{ mb: 3 }}
      >
        Volver a noticias
      </Button>

      <Paper
        sx={{
          p: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        }}
        elevation={3}
      >
        {news.featured_image && (
          <Box
            component="img"
            src={`${API_URL}${news.featured_image}`}
            alt={news.title}
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 2,
              mb: 3,
            }}
          />
        )}

        <Typography
          variant="h3"
          sx={{
            mb: 3,
            fontWeight: 700,
            letterSpacing: '0.05em',
          }}
        >
          {news.title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            color: 'text.secondary',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon fontSize="small" />
            {formatDate(news.published_at)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon fontSize="small" />
            {news.author_name}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityIcon fontSize="small" />
            {news.views} vistas
          </Box>
        </Box>

        {news.categories && news.categories.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {news.categories.map((category) => (
              <Chip
                key={category.id}
                label={category.name}
                color="primary"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: news.content }}
        />

        {news.images && news.images.length > 0 && (
          <Box sx={{ mt: 4 }}>
            {news.images.map((image) => (
              <Box key={image.id} sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src={`${API_URL}${image.image_url}`}
                  alt={image.caption || ''}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                  }}
                />
                {image.caption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      textAlign: 'center',
                      fontStyle: 'italic',
                    }}
                  >
                    {image.caption}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NoticiaDetalle;